import Bugsnag from '@bugsnag/js';
import { hashCode } from '../common/crypto';
import { storageKeys, cacheModule } from '../common/storage';
// eslint-disable-next-line import/no-cycle
import { logger } from '../common/logger';
import { StaticMutexKeys, mutexManager } from '../common/mutexmanager';
import { sendFunctionsDiscountCodeAppliedEvent } from './customEvents/customEventsSender';
import { logRuntimeError } from '../common/utils';

const STOREFRONT_API_VERSION = '2025-01';

const SHOPIFY_CART_VERSION_DATA = 'mrCartVersionData';

const getShopifyCartVersionData = () => {
  try {
    const stringified = sessionStorage.getItem(SHOPIFY_CART_VERSION_DATA);
    if (stringified) {
      const obj = JSON.parse(stringified);
      if (obj) {
        return obj;
      }
    }
    return {};
  } catch (e) {
    logger.log('error in version', e);
    logRuntimeError({
      errorMessage: `${e.name} ${e.message}`,
      errorData: {
        fileName: 'storeFrontCart',
        level: 'error',
        functionName: 'getShopifyCartVersionData',
      },
      errorStack: e,
    });
    return {};
  }
};

const setShopifyCartVersionData = data => {
  sessionStorage.setItem(SHOPIFY_CART_VERSION_DATA, JSON.stringify(data));
};

export const extractCodesFromGraphqlCached = graphqlResponse => {
  let newDiscountCodes = [];
  graphqlResponse.data.cartDiscountCodesUpdate.cart.discountAllocations.forEach(
    ({ code }) => code && newDiscountCodes.push(code.toUpperCase()),
  );
  graphqlResponse.data.cartDiscountCodesUpdate.cart.lines.edges.forEach(
    ({ node }) => {
      node.discountAllocations.forEach(
        ({ code }) => code && newDiscountCodes.push(code.toUpperCase()),
      );
    },
  );
  newDiscountCodes = [...new Set(newDiscountCodes)];
  return newDiscountCodes;
};

export async function createStorefrontCart({
  discountCodes,
  mrCart,
  accessToken,
  countryCode,
  mrOrderID,
  cartToken,
  shopURL,
}) {
  const storefrontCartMutex = mutexManager.getMutex(
    StaticMutexKeys.STOREFRONT_CART,
  );
  // as parallel modification of cart results in inconsistent data, adding mutex
  const shopifyCartMutex = mutexManager.getMutex(StaticMutexKeys.CART_MODIFY);

  try {
    await storefrontCartMutex.acquire();
    const cachedData = cacheModule.get(storageKeys.MR_SHOPIFY_FUNCTIONS_CACHE);
    const {
      resp: cachedResponse,
      hash: cachedHash,
      codes: cachedCodes,
      isStorefrontCart: cachedIsStorefrontCart,
    } = cachedData || {};

    // const cachedResponse = getStorefrontCartResp();
    const newHash = getMrCartHash({ mrCart });
    const isCachedCartSame =
      cachedHash === newHash && stringArrayEquals(cachedCodes, discountCodes);
    if (cachedResponse) {
      if (isCachedCartSame) {
        logger.log('createStorefrontCart', 'returning cached response');
        return {
          resp: cachedResponse,
          isStorefrontCart: cachedIsStorefrontCart,
        };
      }
      logger.log(
        'createStorefrontCart',
        'cached response not matching',
        newHash,
        cachedHash,
      );
    }
    let linesWithErrors = [];

    // shopify uses 2 versions of carts, one is the old one which is not exposed to the storefront api
    // and the other is the new one which is exposed to the storefront api, we need to check if the cart
    // exists in the storefront api, if it doesn't then we need to create it
    // once we have the car, either created or the existing we can apply the discount codes on it

    const existResp = await storefrontCartExists(
      cartToken,
      accessToken,
      shopURL,
    );
    const { isStorefrontCart } = existResp;
    let { id } = existResp;

    if (!isStorefrontCart) {
      logger.log('createStorefrontCart', 'not storefront cart');
      const input = getStoreFrontCartInput({
        mrCart,
        countryCode,
        mrOrderID,
      });
      let resp = await graphqlRequest(
        createCartQuery(),
        { input },
        accessToken,
        shopURL,
      );

      logger.log('createStorefrontCart', 'create cart response', resp);

      const {
        data: {
          cartCreate: { userErrors },
        },
      } = resp;

      // there can be errors in the cart creation, for example if the item is out of stock
      // in that case we need to remove the item from the input and create the cart again
      // we need to track the indexes of the lines which have error, so that based on mr cart we can re add
      // to the response as the ajax cart will still show the items.
      if (userErrors && userErrors.length > 0) {
        const { newInput, errorLines } = handleOutOfStockError({
          userErrors,
          input,
        });
        linesWithErrors = errorLines;
        // we have to re create the cart after removing items because, cart update returns
        // internal server error if the cart has out of stock items
        resp = await graphqlRequest(
          createCartQuery(),
          { input: newInput },
          accessToken,
          shopURL,
        );
      }

      const {
        data: {
          cartCreate: {
            cart: { id: newID },
          },
        },
      } = resp;
      id = newID;
    }

    // as parallel modification of cart results in inconsistent data, adding mutex
    await shopifyCartMutex.acquire();
    const data = await graphqlRequest(
      updateDiscountCodesQuery(),
      { discountCodes, id },
      accessToken,
      shopURL,
    );
    const newCodes = extractCodesFromGraphqlCached(data);

    reportDiscountCodeApplicationErrors({
      data,
      discountCodes,
      id,
    });

    // if there were errors in creating cart for some line items, then add those items from mrCart into
    // the response so that the prices could be displayed correctly
    if (linesWithErrors.length > 0) {
      for (let i = 0; i < linesWithErrors.length; i += 1) {
        const idx = linesWithErrors[i];
        const newEdge = getOutOfStockEdgeFromMrCart({
          item: mrCart[idx],
        });
        data.data.cartDiscountCodesUpdate.cart.lines.edges.splice(
          idx,
          0,
          newEdge,
        );
      }
    }

    cacheModule.set(storageKeys.MR_SHOPIFY_FUNCTIONS_CACHE, {
      resp: data,
      hash: newHash,
      codes: newCodes,
      isStorefrontCart,
    });

    sendFunctionsDiscountCodeAppliedEvent(mrCart);

    return { resp: data, isStorefrontCart };
  } catch (e) {
    logger.log('createStorefrontCart', e);
    logRuntimeError({
      errorMessage: `${e.name} ${e.message}`,
      errorData: {
        fileName: 'storeFrontCart',
        level: 'error',
      },
      errorStack: e,
    });
    Bugsnag.notify(e);
  } finally {
    storefrontCartMutex.release();
    shopifyCartMutex.release();
  }
  return { resp: null, isStorefrontCart: false };
}

function handleOutOfStockError({ userErrors, input }) {
  const errorLines = [];
  for (let i = 0; i < userErrors.length; i += 1) {
    const error = userErrors[i];
    const { field } = error;

    if (
      field &&
      field.length >= 3 &&
      field[0] === 'input' &&
      field[1] === 'lines'
    ) {
      const lineIndex = field[2];
      errorLines.push(parseInt(lineIndex, 10));
    }
  }

  const newInput = { ...input };
  const newLines = [];
  for (let i = 0; i < input.lines.length; i += 1) {
    if (errorLines.indexOf(i) === -1) {
      newLines.push(input.lines[i]);
    }
  }
  newInput.lines = newLines;
  return { newInput, errorLines };
}

function getOutOfStockEdgeFromMrCart({ item }) {
  const {
    sp_product_id,
    sp_variant_id,
    properties_order,
    properties,
    price,
    quantity,
    selling_plan_allocation,
  } = item;
  const attributes =
    properties_order.map(key => ({
      key,
      value: properties[key],
    })) || [];

  let sellingPlanAllocation = null;
  if (selling_plan_allocation) {
    const { selling_plan } = selling_plan_allocation;
    if (selling_plan) {
      const { id } = selling_plan;
      if (id) {
        sellingPlanAllocation = {
          sellingPlan: { id: `gid://shopify/SellingPlan/${id}` },
        };
      }
    }
  }

  const edge = {
    node: {
      attributes,
      cost: {
        amountPerQuantity: {
          amount: price.toString(),
        },
        subtotalAmount: {
          amount: (price * quantity).toString(),
        },
        totalAmount: {
          amount: (price * quantity).toString(),
        },
        compareAtAmountPerQuantity: {
          amount: null,
        },
      },
      discountAllocations: [],
      merchandise: {
        id: `gid://shopify/ProductVariant/${sp_variant_id}`,
        product: {
          id: `gid://shopify/Product/${sp_product_id}`,
        },
      },
      quantity,
      sellingPlanAllocation,
    },
  };
  return edge;
}

function getMrCartHash({ mrCart }) {
  const cleaned = [...mrCart].map(item => {
    const {
      // key,
      // discounted_price,
      line_level_discount_allocations,
      ...rest
    } = item;

    return rest;
  });

  return hashCode(JSON.stringify(cleaned));
}
function getStoreFrontCartInput({ mrCart, countryCode, mrOrderID }) {
  // we need to reverse the cart because the cart is added in reverse order
  const mrCartToUse = [...mrCart].reverse();
  const input = {
    attributes: [
      {
        key: '_mrOrderID',
        value: mrOrderID,
      },
    ],
    lines: [],
    buyerIdentity: {
      countryCode,
    },
  };
  for (let i = 0; i < mrCartToUse.length; i += 1) {
    const {
      sp_variant_id,
      quantity,
      properties,
      selling_plan_allocation,
    } = mrCartToUse[i];

    const line = {
      merchandiseId: `gid://shopify/ProductVariant/${sp_variant_id}`,
      quantity,
    };

    if (
      selling_plan_allocation &&
      selling_plan_allocation.selling_plan &&
      selling_plan_allocation.selling_plan.id
    ) {
      line.sellingPlanId = `gid://shopify/SellingPlan/${
        selling_plan_allocation.selling_plan.id
      }`;
    }

    if (properties && Object.keys(properties).length > 0) {
      // filter out properties which don't have value
      const filteredProperties = Object.keys(properties).reduce((acc, key) => {
        if (properties[key]) {
          acc[key] = properties[key];
        }
        return acc;
      }, {});

      if (filteredProperties && Object.keys(filteredProperties).length > 0) {
        line.attributes = Object.keys(properties).map(key => ({
          key,
          value: properties[key],
        }));
      }
    }

    input.lines.push(line);
  }
  return input;
}

async function graphqlRequest(query, variables, token, shopURL) {
  let body = { query };
  try {
    if (variables) {
      body = { ...body, variables };
    }

    let baseURL = '';
    if (
      shopURL &&
      window.location.host &&
      window.location.host.includes('shopifypreview')
    ) {
      baseURL = `https://${shopURL}`;
      if (shopURL.startsWith('https://')) {
        baseURL = shopURL;
      }
    }

    const options = {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify(body),
    };

    const resp = await fetch(
      `${baseURL}/api/${STOREFRONT_API_VERSION}/graphql.json`,
      options,
    );
    if (resp.status === 200) {
      const data = await resp.json();
      return data;
    }
  } catch (e) {
    logger.log('error in graphql request', e);
    logRuntimeError({
      errorMessage: 'graphql exception',
      errorData: {
        fileName: 'storeFrontCart',
        functionName: 'graphqlRequest',
        level: 'error',
        extraErrorData: {
          body,
        },
      },
      errorStack: e,
    });
  }
  return null;
}

const createCartQuery = () => {
  const query = `
    mutation cartCreate($input: CartInput) {
        cartCreate(input: $input) {
            cart {
                id
            }
            userErrors {
                field
                message
            }
        }
    }`;
  return query;
};

const updateDiscountCodesQuery = () => {
  const query = `
  mutation cartDiscountCodesUpdate($id: ID!, $discountCodes:[String!]) {
    cartDiscountCodesUpdate(cartId: $id, discountCodes: $discountCodes) {
      cart {
        id
        attributes {
          key
          value
        }
        checkoutUrl
        lines(first:100) {
          edges {
            node {
              attributes {
                key
                value
              }
              cost {
                totalAmount{
                    amount
                }
                subtotalAmount{
                    amount
                }
                amountPerQuantity{
                    amount
                }
                compareAtAmountPerQuantity {
                  amount
                }
              }
              discountAllocations {
                discountedAmount {
                    amount
                }
                ... on CartCodeDiscountAllocation {
                    code
                }
                ... on CartAutomaticDiscountAllocation {
                  title
                }
              }
              id
              merchandise {
                ... on ProductVariant {
                    __typename
                    id
                    product{
                      id
                    }
                }
              }
              quantity
              sellingPlanAllocation {
                sellingPlan {
                  id
                  name
                }
              }
            }
          }
        }
        discountAllocations {
          discountedAmount {
              amount
          }
          ... on CartAutomaticDiscountAllocation {
            title
          }
          ... on CartCodeDiscountAllocation {
            code
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }`;
  return query;
};

const storefrontCartExistsQuery = () => {
  const query = `
  query cart($id: ID!) {
    cart(id: $id) {
      id
    }
  }`;
  return query;
};

function getPathFromUrl(url) {
  return url.split('?')[0];
}

const checkKeyExistInId = (id, key) => id.includes(key);

const isKeyExistInAllIdsAndEqual = (ids, key) => {
  // checking if all keys exist in the ids
  const isKeyExist = ids.every(
    cartToken => cartToken && checkKeyExistInId(cartToken, key),
  );
  // comparing if all keys are equal
  return isKeyExist && ids.every(id => id === ids[0]);
};

const isKeyExistInAnyIdAndEqual = (ids, key) => {
  // checking if any key exist in the ids and creating new map with the right id
  const idsWithoutKeyString = ids.map(
    id => id && (checkKeyExistInId(id, key) ? getPathFromUrl(id) : id),
  );
  // comparing with first id because all ids should be equal
  return idsWithoutKeyString.every(id => id === idsWithoutKeyString[0]);
};
const storefrontCartExists = async (id, accessToken, shopURL) => {
  const { token, isStorefrontCart } = getShopifyCartVersionData();

  const tokens = [token, id].map(t => {
    // if the token included gid then we need to extract
    if (t && !t.includes('gid')) {
      return getGraphQLID(t, GQL_RESOURCE_CART);
    }
    return t;
  });
  const keyString = 'key';

  // checks two things
  // 1. if key exists in the tokens and they are equal
  // or
  // 2. if a key exists in any id and they are equal
  if (
    isKeyExistInAllIdsAndEqual(tokens, keyString) ||
    isKeyExistInAnyIdAndEqual(tokens, keyString)
  ) {
    return { isStorefrontCart, id: token };
  }

  const resp = await graphqlRequest(
    storefrontCartExistsQuery(),
    { id: getGraphQLID(id, GQL_RESOURCE_CART) },
    accessToken,
    shopURL,
  );

  if (resp) {
    const {
      data: { cart },
    } = resp;

    let respID = null;
    const exists = cart !== null;
    if (exists) {
      respID = cart.id;
      setShopifyCartVersionData({ token: cart.id, isStorefrontCart: exists });
    }
    return { isStorefrontCart: exists, id: respID };
  }
  return { isStorefrontCart: false, id: null };
};

const stringArrayEquals = (arr1, arr2) => {
  if (arr1.length !== arr2.length) {
    return false;
  }

  const sortedArr1 = arr1.sort();
  const sortedArr2 = arr2.sort();

  for (let i = 0; i < sortedArr1.length; i += 1) {
    if (sortedArr1[i] !== sortedArr2[i]) {
      return false;
    }
  }

  return true;
};

const getGraphQLID = (id, resource) => `gid://shopify/${resource}/${id}`;
const GQL_RESOURCE_CART = 'Cart';

const reportDiscountCodeApplicationErrors = ({ data, discountCodes, id }) => {
  try {
    const {
      data: {
        cartDiscountCodesUpdate: { userErrors },
      },
    } = data;

    if (userErrors && userErrors.length > 0) {
      Bugsnag.notify(new Error('Discount code application error'), evt => {
        evt.addMetadata('custom_erros', {
          userErrors,
          discountCodes,
          cartToken: id,
        });
      });
      const error = new Error('Discount code application error');
      error.errorData = {
        userErrors,
        discountCodes,
        cartToken: id,
      };
      throw error;
    }
  } catch (e) {
    logger.log('error in reporting', e);
    logRuntimeError({
      errorMessage: `${e.name} ${e.message}`,
      errorData: e.errorData,
      errorStack: e,
    });
  }
};
